import type { ProjectType, ProjectTypeProfile } from '../../core/types/data-models';

/**
 * The project-type profiles (design plan §3.2). They live here until
 * the backend serves them from `GET /project-types`; only the fields the
 * onboarding wizards and the validation page read are included, in the
 * shape that endpoint will return, so nothing downstream changes when this
 * file becomes a query.
 */
export const PROJECT_TYPE_PROFILES: ProjectTypeProfile[] = [
  {
    type: 'IDEA_VALIDATION',
    name: 'Guided Idea Validation',
    tagline: 'Turn a new idea into a validated product plan.',
    audience: 'founders, entrepreneurs, product managers and vibe coders with a new idea',
    inputs: {
      description: 'required',
      websiteUrl: 'optional',
      githubUrl: 'hidden',
      docsUrl: 'hidden',
      linkedinUrl: 'hidden',
    },
    // The first stage of the guided validation (IDEA_VALIDATION category, order 1).
    kickoffDocumentType: 'IV_PROBLEM_PAIN',
    validationCategory: 'IDEA_VALIDATION',
    start: 'generate-kickoff',
  },
  {
    type: 'BUSINESS_VALIDATION',
    name: 'Business Validation for Investing & M&A',
    tagline: 'Evaluate an existing company or product before you invest or acquire.',
    audience: 'investors, acquirers and analysts evaluating an existing business',
    inputs: {
      description: 'optional',
      websiteUrl: 'required',
      githubUrl: 'optional',
      docsUrl: 'optional',
      linkedinUrl: 'optional',
    },
    // The first stage of the business validation (BUSINESS_VALIDATION
    // category, order 1); the source analysis chains it once the brief exists.
    kickoffDocumentType: 'BV_COMPANY_SNAPSHOT',
    validationCategory: 'BUSINESS_VALIDATION',
    start: 'analyze-sources',
  },
  {
    type: 'STOCK_VALIDATION',
    name: 'Stock Validation',
    tagline: 'Decide whether to buy, hold or sell a listed stock.',
    audience: 'investors and analysts researching a publicly listed company',
    // The exchange and the symbol are the only inputs; the wizard writes them
    // into the description, which becomes the project prompt.
    inputs: {
      description: 'required',
      websiteUrl: 'hidden',
      githubUrl: 'hidden',
      docsUrl: 'hidden',
      linkedinUrl: 'hidden',
    },
    // The first stage of the stock validation (STOCK_VALIDATION category, order 1).
    kickoffDocumentType: 'SV_COMPANY_SNAPSHOT',
    validationCategory: 'STOCK_VALIDATION',
    start: 'generate-kickoff',
  },
  {
    type: 'STRATEGY_MANAGEMENT',
    name: 'Business Strategy Management',
    tagline: 'Keep the strategy of an in-market product current as the market moves.',
    audience: 'C-level, VPs, Marketing and Product Leaders running an in-market product',
    inputs: {
      description: 'optional',
      websiteUrl: 'required',
      githubUrl: 'optional',
      docsUrl: 'optional',
      linkedinUrl: 'optional',
    },
    kickoffDocumentType: 'PRODUCT_ANALYSIS',
    validationCategory: null,
    start: 'generate-kickoff',
  },
];

/**
 * An idea validation project is used only through its validation wizard:
 * the sidebar drops the overview and the validation flow, the overview
 * route opens the wizard, and the wizard's summary ends with the project's
 * entity types and market radar instead.
 */
export function isWizardOnlyProject(type: ProjectType | null | undefined): boolean {
  return type === 'IDEA_VALIDATION';
}

export function getProjectTypeProfile(type: ProjectType): ProjectTypeProfile {
  const profile = PROJECT_TYPE_PROFILES.find((p) => p.type === type);
  if (!profile) throw new Error(`No onboarding profile for project type "${type}"`);
  return profile;
}
